import React, { useState, useCallback, useMemo, useRef } from 'react';
import { Box, type BoxProps } from '@mui/material';
import { Trans } from '@lingui/macro';
import { PencilSimple, X, Check, CaretDown } from '@phosphor-icons/react';
import { IconButton } from 'components/button';
import { Switch } from 'components/inputs';
import { Text } from 'components/text';
import { CustomDropDown } from 'features/misc/table';
import type { ComboBoxOption } from 'components/inputs/types';
import { useForm } from 'features/form';
import type { Fields } from 'features/form';
import * as yup from 'yup';
import type {
  PageHeaderFieldType,
  PageHeaderField,
  PageHeaderSection,
  PageHeaderProps,
} from './PageHeader.types';
import {
  headerContainerBaseSx,
  HeaderContainer,
  SectionContainer,
  Divider,
  SectionHeader,
  FieldsContainer,
  baseFieldContainerSx,
  FieldContainer,
  FieldLabel,
  FieldValue,
  IconWrapper,
  IndicatorWrapper,
  ActionButtons,
  CancelLink,
  SaveLink,
  FieldInputWrapper,
  CustomDropdownWrapper,
  SwitchWrapper,
  SwitchValue,
  StyledSelect,
  pageHeaderDropDownTriggerSx,
  StyledTextInput,
} from './PageHeader.styles';

export type {
  PageHeaderFieldType,
  CustomDropdownConfig,
  PageHeaderField,
  PageHeaderSection,
  PageHeaderProps,
} from './PageHeader.types';

// Helper function to safely convert a value to ReactNode
const toReactNode = (value: unknown): React.ReactNode => {
  if (value == null || value === '') {
    return '--';
  }
  return value as React.ReactNode;
};

// Helper function to safely convert a value to string for input components
const toStringValue = (value: unknown): string => {
  return value == null ? '' : String(value);
};

// Helper function to initialize form values from section fields
const initializeFormValues = (fields: PageHeaderField[]): Record<string, unknown> => {
  const initialValues: Record<string, unknown> = {};
  fields.forEach(field => {
    const key = field.fieldKey || String(field.label);
    if (field.initialValue !== undefined) {
      initialValues[key] = field.initialValue;
    } else if (field.type === 'customDropdown' && field.options) {
      const multiple = field.customDropdown?.multiple ?? false;
      if (multiple) {
        const arr = Array.isArray(field.value)
          ? field.value
          : field.value != null && field.value !== ''
            ? [field.value]
            : [];
        initialValues[key] = arr;
      } else {
        const matchingOption = field.options.find(
          opt => opt.id === field.value || opt.value === field.value || opt.label === field.value
        );
        // Use matching option when found; when value is intentionally empty (null/''), keep it so "Select" placeholder and saving null/empty work
        const valueIsEmpty = field.value == null || field.value === '';
        initialValues[key] = matchingOption?.id ?? (valueIsEmpty ? '' : field.value ?? '');
      }
    } else if (field.type === 'select' && field.options) {
      // For select fields, try to find matching option by value or use first option
      const matchingOption = field.options.find(
        opt => opt.id === field.value || opt.value === field.value || opt.label === field.value
      );
      initialValues[key] = matchingOption?.id || field.options[0]?.id || field.value;
    } else {
      initialValues[key] = field.value;
    }
  });
  return initialValues;
};

// Map PageHeader section fields to useForm Fields shape (for performance: useForm used elsewhere)
const buildFieldsForUseForm = (fields: PageHeaderField[]): Fields => {
  const acc: Fields = {};
  fields.forEach(field => {
    const key = field.fieldKey || String(field.label);
    const type = field.type ?? 'readonly';
    const useFormType =
      type === 'select'
        ? 'dd'
        : type === 'customDropdown'
          ? 'value'
          : type === 'switch' || type === 'readonly'
            ? 'value'
            : type === 'number'
              ? 'number'
              : 'text';
    acc[key] = {
      type: useFormType,
      id: key,
      ...(field.options && { options: field.options }),
    };
  });
  return acc;
};

// Permissive validation so header edit never blocks save (no required rules)
const buildValidationsForUseForm = (
  fields: PageHeaderField[]
): yup.ObjectSchema<Record<string, unknown>> => {
  const shape: Record<string, yup.Schema<unknown>> = {};
  fields.forEach(field => {
    const key = field.fieldKey || String(field.label);
    shape[key] = yup.mixed().optional();
  });
  return yup.object().shape(shape);
};

// Get section width based on index and total sections
const getSectionWidth = (index: number, totalSections: number): number => {
  if (totalSections === 1) return 100;
  if (index === 0) {
    return totalSections === 2 ? 69 : 100 / totalSections;
  }
  return totalSections === 2 ? 31 : 100 / totalSections;
};

// Renders a single field (view or edit). Kept outside section component so it can be pure.
const renderField = (
  field: PageHeaderField,
  fieldIndex: number,
  isEditing: boolean,
  sectionFormValues: Record<string, unknown>,
  onFieldChange: (fieldKey: string, value: unknown, fieldType?: PageHeaderFieldType) => void,
  sectionIndex: number,
  dataTestId: string,
  fieldsSx?: BoxProps['sx']
) => {
  const fieldKey = field.fieldKey || String(field.label);
  const fieldType = field.type || 'readonly';

  let displayValue = field.value;
  if (!isEditing && fieldType === 'select' && field.options) {
    const matchingOption = field.options.find(
      opt => opt.id === field.value || opt.value === field.value || opt.label === field.value
    );
    displayValue = matchingOption?.label || field.value;
  }
  if (!isEditing && fieldType === 'customDropdown' && field.options) {
    const multiple = field.customDropdown?.multiple ?? false;
    if (multiple) {
      const ids = Array.isArray(field.value)
        ? field.value
        : field.value != null && field.value !== ''
          ? [field.value]
          : [];
      displayValue =
        ids
          .map(id => field.options?.find(opt => opt.id === id || opt.value === id)?.label ?? id)
          .filter(Boolean)
          .join(', ') || '--';
    } else {
      const matchingOption = field.options.find(
        opt => opt.id === field.value || opt.value === field.value || opt.label === field.value
      );
      displayValue = matchingOption?.label || field.value;
    }
  }
  // Edit mode: use form state (primitives) so controls work; view mode: use section display value
  const fieldValue = isEditing ? sectionFormValues[fieldKey] : displayValue;
  const fieldTestId = `${dataTestId}_section_${sectionIndex}_field_${fieldIndex}`;
  const reactKey = `${sectionIndex}-${fieldIndex}-${fieldKey}`;

  if (isEditing && fieldType !== 'readonly') {
    const fieldContainerSx = {
      ...baseFieldContainerSx,
      ...fieldsSx,
      ...field.sx,
    } as BoxProps['sx'];
    return (
      <FieldContainer key={reactKey} data-testid={fieldTestId} sx={fieldContainerSx}>
        <FieldLabel variant="bodySm" weight="regular" color="placeholder">
          {typeof field.label === 'string' ? <Trans>{field.label}</Trans> : field.label}
        </FieldLabel>
        <FieldInputWrapper>
          {fieldType === 'switch' && (
            <SwitchWrapper sx={field.switchWrapperSx}>
              <Switch
                value={!!fieldValue}
                onChange={(e, checked) => onFieldChange(fieldKey, checked)}
                size="small"
              />
              <SwitchValue variant="bodySm" weight="regular" color="secondary">
                {fieldValue === true || fieldValue === 'Yes' || fieldValue === 'yes' ? (
                  <Trans>Yes</Trans>
                ) : (
                  <Trans>No</Trans>
                )}
              </SwitchValue>
            </SwitchWrapper>
          )}
          {fieldType === 'select' && (
            <StyledSelect
              value={toStringValue(fieldValue)}
              onChange={e => {
                onFieldChange(fieldKey, e.target.value);
              }}
              options={field.options || []}
              variant="standard"
              placeholder={field?.placeholder}
            />
          )}
          {fieldType === 'customDropdown' && (
            <CustomDropdownWrapper
              width={field.customDropdown?.width}
              height={field.customDropdown?.height}
              flexible={field.customDropdown?.flexible ?? field.customDropdown?.width == null}
              sx={field.customDropdown?.sx}
            >
              <CustomDropDown
                size="sm"
                options={(field.options || []) as ComboBoxOption[]}
                value={
                  field.customDropdown?.multiple
                    ? (Array.isArray(fieldValue)
                        ? fieldValue
                        : fieldValue != null && fieldValue !== ''
                          ? [String(fieldValue)]
                          : []
                      ).map(id => ({ id }))
                    : fieldValue != null && fieldValue !== ''
                      ? [{ id: String(fieldValue) }]
                      : []
                }
                onChange={(_e, newValue) => {
                  const ids = newValue.map(o => o?.id ?? '').filter(Boolean);
                  onFieldChange(fieldKey, field.customDropdown?.multiple ? ids : ids[0] ?? '');
                }}
                singleSelect={!field.customDropdown?.multiple}
                enableSearch={field.customDropdown?.searchable ?? false}
                alignMenuWidthToTrigger={field.customDropdown?.alignMenuWidthToTrigger}
                width={
                  field.customDropdown?.width != null &&
                  typeof field.customDropdown.width === 'number'
                    ? field.customDropdown.width
                    : 280
                }
                menuWidth={field.customDropdown?.menuWidth}
                placeholder={field?.placeholder ?? ''}
                searchPlaceholder={field?.placeholder ?? ''}
                renderValue={
                  !field.customDropdown?.multiple
                    ? value => (value?.[0]?.label != null ? value[0].label : null)
                    : undefined
                }
                triggerHeight={field.customDropdown?.triggerHeight}
                triggerSx={() => pageHeaderDropDownTriggerSx}
                triggerEndIcon={() => <CaretDown size={20} />}
              />
            </CustomDropdownWrapper>
          )}
          {fieldType === 'text' && (
            <StyledTextInput
              value={toStringValue(fieldValue)}
              onChange={e => onFieldChange(fieldKey, e.target.value)}
              variant="standard"
              fullWidth
              placeholder={field?.placeholder}
            />
          )}
          {fieldType === 'number' && (
            <StyledTextInput
              type="text"
              value={toStringValue(fieldValue)}
              onChange={e => onFieldChange(fieldKey, e.target.value, fieldType)}
              variant="standard"
              fullWidth
              placeholder={field?.placeholder}
              InputProps={{
                inputProps: {
                  inputMode: 'numeric',
                  pattern: '[0-9]*' as const,
                  ...(field.maxLength !== undefined && { maxLength: field.maxLength }),
                },
              }}
            />
          )}
        </FieldInputWrapper>
      </FieldContainer>
    );
  }

  const readOnlyFieldContainerSx = {
    ...baseFieldContainerSx,
    ...fieldsSx,
    ...field.sx,
  } as BoxProps['sx'];
  return (
    <FieldContainer key={reactKey} data-testid={fieldTestId} sx={readOnlyFieldContainerSx}>
      <FieldLabel variant="bodySm" weight="regular" color="placeholder">
        {typeof field.label === 'string' ? <Trans>{field.label}</Trans> : field.label}
      </FieldLabel>
      <FieldValue variant="bodySm" weight="regular">
        {field.icon && <IconWrapper>{field.icon}</IconWrapper>}
        <span>
          {fieldType === 'switch' ? (
            fieldValue === true || fieldValue === 'Yes' || fieldValue === 'yes' ? (
              <Trans>Yes</Trans>
            ) : (
              <Trans>No</Trans>
            )
          ) : (
            toReactNode(fieldValue)
          )}
        </span>
        {field.indicator && <IndicatorWrapper>{field.indicator}</IndicatorWrapper>}
      </FieldValue>
    </FieldContainer>
  );
};

type PageHeaderSectionProps = {
  section: PageHeaderSection;
  index: number;
  totalSections: number;
  dataTestId: string;
};

/**
 * One section of the header. Uses useForm (as used elsewhere) so state updates
 * are handled by the form hook and only this section re-renders when editing.
 */
const emptyFormValidations = yup.object();

const PageHeaderSectionBlock = ({
  section,
  index,
  totalSections,
  dataTestId,
}: PageHeaderSectionProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [disableSave, setDisableSave] = useState(false);
  const isEditable = section.editable === true;

  const fieldsConfig = useMemo(
    () => (isEditable ? buildFieldsForUseForm(section.fields) : {}),
    [isEditable, section.fields]
  );
  const validations = useMemo(
    () => (isEditable ? buildValidationsForUseForm(section.fields) : emptyFormValidations),
    [isEditable, section.fields]
  );
  const initialValues = useMemo(
    () => (isEditable ? initializeFormValues(section.fields) : {}),
    [isEditable, section.fields]
  );

  // Freeze fields + initialValues while editing so parent re-renders (e.g. after save/refetch)
  // don't trigger useForm's effect ([fields, initialValues]) and reset the form to old data
  const frozenRef = useRef<{ fields: typeof fieldsConfig; initialValues: typeof initialValues }>({
    fields: fieldsConfig,
    initialValues,
  });
  if (!isEditing) {
    frozenRef.current = { fields: fieldsConfig, initialValues };
  }
  const fieldsForForm = isEditing ? frozenRef.current.fields : fieldsConfig;
  const initialValuesForForm = isEditing ? frozenRef.current.initialValues : initialValues;

  const {
    fields: formFields,
    submit: formSubmit,
    resetForm,
  } = useForm({
    fields: fieldsForForm,
    validations,
    initialValues: initialValuesForForm,
  });

  const formValues = useMemo(
    () => Object.fromEntries(Object.entries(formFields).map(([k, f]) => [k, f.value])),
    [formFields]
  );

  const handleEdit = useCallback(() => {
    setIsEditing(true);
    setDisableSave(false);
    section.onEdit?.(); // optional: parent can be notified when edit starts
  }, [section]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    resetForm({ clearValues: false }); // restore to initial so Edit again shows correct values
    section?.onCancel?.(initialValues);
    setDisableSave(false);
  }, [section, initialValues, resetForm]);

  const handleSave = useCallback(async () => {
    const { errors, data } = await formSubmit();
    if (errors?.length) return;
    if (section?.onSave && data) {
      setDisableSave(true);
      const result = await section.onSave(data as Record<string, unknown>);
      if (result) {
        setIsEditing(false);
        return;
      }
      return;
    }
    setIsEditing(false);
  }, [section, formSubmit]);

  const handleFieldChange = useCallback(
    (fieldKey: string, value: unknown, fieldType?: PageHeaderFieldType) => {
      let filteredValue = value;
      if (fieldType === 'number') {
        const stringValue = String(value ?? '');
        filteredValue = stringValue.replace(/[^0-9]/g, '');
      }
      formFields[fieldKey]?.onChange?.(filteredValue);
      const field = section.fields.find(f => (f.fieldKey || String(f.label)) === fieldKey);
      field?.onChange?.(filteredValue);
      setDisableSave(false);
    },
    [section, formFields]
  );

  const sectionTestId = section['data-testid'] || `${dataTestId}_section_${index}`;
  const cancelTestId = `${dataTestId}_section_${index}_cancel`;
  const saveTestId = `${dataTestId}_section_${index}_save`;
  const editTestId = `${dataTestId}_section_${index}_edit`;
  const columns = section.columns ?? (index === 0 ? 3 : 2);
  const sectionWidth = section?.width ?? getSectionWidth(index, totalSections);
  const showDividerBefore =
    section.showDividerBefore !== undefined ? section.showDividerBefore : index > 0;
  const showDividerAfter = section.showDividerAfter ?? false;
  const dividerKey = section['data-testid']
    ? `divider-${section['data-testid']}`
    : `divider-${String(section.title)}-${index}`;

  const renderDivider = (customStyle?: React.CSSProperties) => {
    if (section.divider) {
      return <React.Fragment key={dividerKey}>{section.divider}</React.Fragment>;
    }
    return <Divider key={dividerKey} style={customStyle || section.dividerStyle} />;
  };

  const sectionKey = section['data-testid'] || `section-${String(section.title)}-${index}`;

  const fieldElements = useMemo(
    () =>
      section.fields.map((feild, fieldIndex) =>
        renderField(
          feild,
          fieldIndex,
          isEditing,
          formValues,
          handleFieldChange,
          index,
          dataTestId,
          section.fieldsSx
        )
      ),
    [section.fields, isEditing, section.fieldsSx, formValues, handleFieldChange, index, dataTestId]
  );

  return (
    <React.Fragment key={sectionKey}>
      {showDividerBefore && renderDivider()}
      <SectionContainer width={sectionWidth} data-testid={sectionTestId} sx={section.sx}>
        <SectionHeader sx={section.headerSx}>
          <Text variant="labelMd" weight="semiBold" color="primary">
            {section.title}
          </Text>
          {isEditing ? (
            <ActionButtons>
              <CancelLink type="button" onClick={handleCancel} data-testid={cancelTestId}>
                <X size={15} weight="light" />
                <span>Cancel</span>
              </CancelLink>
              <SaveLink
                type="button"
                onClick={handleSave}
                data-testid={saveTestId}
                disabled={disableSave}
              >
                <Check width={15} height={15} />
                <span>Save changes</span>
              </SaveLink>
            </ActionButtons>
          ) : (
            section.editable === true &&
            (section.onSave ?? section.onEdit) !== undefined && (
              <IconButton size="sm" onClick={handleEdit} data-testid={editTestId}>
                <PencilSimple size={20} />
              </IconButton>
            )
          )}
        </SectionHeader>
        <FieldsContainer columns={columns} sx={{ gap: '24px', ...section.fieldsContainerSx }}>
          {fieldElements}
        </FieldsContainer>
      </SectionContainer>
      {showDividerAfter && renderDivider()}
    </React.Fragment>
  );
};

export const PageHeader = ({
  sections: sectionsProp,
  primarySection,
  secondarySection,
  'data-testid': dataTestId = 'page-header',
  sx: rootSx,
}: PageHeaderProps) => {
  // Convert old API to new API for backward compatibility
  const sections =
    sectionsProp ??
    (primarySection ? [primarySection, ...(secondarySection ? [secondarySection] : [])] : []);

  if (sections.length === 0) {
    return null;
  }

  return (
    <HeaderContainer
      data-testid={dataTestId}
      sx={{ ...headerContainerBaseSx, ...rootSx } as BoxProps['sx']}
    >
      {sections.map((section, index) => (
        <PageHeaderSectionBlock
          key={section['data-testid'] || `section-${String(section.title)}-${index}`}
          section={section}
          index={index}
          totalSections={sections.length}
          dataTestId={dataTestId}
        />
      ))}
    </HeaderContainer>
  );
};
